import type React from "react";
import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Typography,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Switch,
  InputAdornment,
  Paper,
  Chip,
  Divider,
  Badge,
  ListItemAvatar,
  Avatar,
  Snackbar,
  Alert,
  useTheme,
  Tooltip,
  CircularProgress,
  Card,
  CardContent,
} from "@mui/material";
import {
  Add,
  Search,
  Language,
  Notifications,
  Check,
  Close,
  CloudUpload,
  TagOutlined,
  Chat,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Cancel,
  AddCircleOutline,
} from "@mui/icons-material";
import { ChannelService } from "../../services/channelService";
import { ChannelDto } from "../../types/channel";
import { ChannelInviteDto } from "../../types/channelInvite";
import { ChannelMessageDto } from "../../types/channelMessage";
import { ProfileDto } from "../../types/UserToken";
import { useMediaQuery } from "@mui/system";
import { ChannelJoinDialog } from "./dialogs/ChannelJoinDialog";
import { useDropzone } from "react-dropzone";

interface ChannelSidebarProps {
  selectedTab: "channels" | "global";
  onTabChange: (tab: "channels" | "global") => void;
  selectedChannel: ChannelDto | null;
  onChannelSelect: (channel: ChannelDto) => void;
  onCreateChannel: (channel: ChannelDto) => void;
  currentUser: ProfileDto;
  globalMessages: ChannelMessageDto[];
  handleShowChats: () => void;
  publicChannels: ChannelDto[];
  channels?: ChannelDto[];
  fetchChannels?: () => Promise<void>;
  fetchPublicChannels?: () => Promise<void>;
  setChannels?: React.Dispatch<React.SetStateAction<ChannelDto[]>>;
  setSelectedChannel?: React.Dispatch<React.SetStateAction<ChannelDto | null>>;
  fetchChannelData?: () => Promise<void>;
}

export default function ChannelSidebar({
  selectedTab,
  onTabChange,
  selectedChannel,
  onChannelSelect,
  onCreateChannel,
  currentUser,
  handleShowChats,
  publicChannels = [],
  channels = [],
  fetchChannels = async () => {},
  fetchPublicChannels = async () => {},
  setChannels = () => {},
  setSelectedChannel = () => {},
  fetchChannelData = async () => {},
}: ChannelSidebarProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newChannelName, setNewChannelName] = useState("");
  const [newChannelDescription, setNewChannelDescription] = useState("");
  const [isPublic, setIsPublic] = useState(true);
  const [requiresInvite, setRequiresInvite] = useState(false);
  const [searchResults, setSearchResults] = useState<ChannelDto[] | null>(null);
  const [pendingInvites, setPendingInvites] = useState<ChannelInviteDto[]>([]);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [whatsappLink, setWhatsappLink] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [channelJoinDialogOpen, setChannelJoinDialogOpen] = useState(false);
  const [channelToJoin, setChannelToJoin] = useState<ChannelDto | null>(null);
  const [showChannels, setShowChannels] = useState(false);
  const [showPublicChannels, setShowPublicChannels] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
    variant: "filled" | "outlined" | "standard";
  }>({
    open: false,
    message: "",
    severity: "success",
    variant: "filled",
  });

  const filteredChannels = channels.filter((channel) =>
    channel.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredPublicChannels = publicChannels.filter((channel) =>
    channel.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  console.log("search:", searchResults);

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newValue: "channels" | "global"
  ) => {
    onTabChange(newValue);
  };

  const resetForm = () => {
    setNewChannelName("");
    setNewChannelDescription("");
    setIsPublic(false);
    setRequiresInvite(false);
    setSelectedImage(null);
    setPreviewImage(null);
    setWhatsappLink("");
    setError(null);
  };

  const handleCreateChannel = async () => {
    if (!newChannelName.trim()) {
      setError("El nombre del canal es obligatorio");
      return;
    }

    setIsSubmitting(true);
    const formData = new FormData();
    formData.append("Name", newChannelName.trim().toLowerCase());
    formData.append("Description", newChannelDescription.trim());
    formData.append("IsPublic", isPublic.toString());
    formData.append("RequiresInvite", requiresInvite.toString());
    formData.append("WhatsappLink", whatsappLink.trim());
    formData.append("CreatedByProfileId", currentUser.id.toString());
    formData.append("InitialMember.ProfileId", currentUser.id.toString());
    formData.append("InitialMember.IsAdmin", "true");
    if (selectedImage) {
      formData.append("ImageUrl", selectedImage);
    }

    try {
      const newChannel = await ChannelService.createChannel(formData);
      if (newChannel.success) {
        onCreateChannel(newChannel.data);
        setCreateDialogOpen(false);
        setChannels((prev) => [...prev, newChannel.data]);
        setSelectedChannel(newChannel.data);
        resetForm();
        setSnackbar({
          open: true,
          message: "El canal se ha creado exitosamente",
          severity: "success",
          variant: "standard",
        });
      }
    } catch (error) {
      console.error("Error al crear canal:", error);
      setError("Hubo un error al crear el canal");
    } finally {
      setIsSubmitting(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { "image/*": [] },
    onDrop: (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        setSelectedImage(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewImage(reader.result as string);
        };
        reader.readAsDataURL(file);
      }
    },
  });

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setPreviewImage(null);
  };

  const fetchInvites = useCallback(async () => {
    if (!currentUser.id) return;
    try {
      const invites = await ChannelService.getPendingInvites(currentUser.id);
      setPendingInvites(invites);
    } catch (error) {
      console.error("Error cargando invitaciones:", error);
    }
  }, [currentUser.id]);

  useEffect(() => {
    fetchChannels();
    fetchInvites();
  }, [fetchInvites, fetchChannels]);

  useEffect(() => {
    fetchPublicChannels();
  }, [fetchPublicChannels]);

  const invitesSentByMe = pendingInvites.filter(
    (i) => i.invitedByProfileId === currentUser.id && !i.isRequestToJoin
  );
  const invitesToMe = pendingInvites.filter(
    (i) => i.invitedProfileId === currentUser.id && !i.isRequestToJoin
  );
  const joinRequestsToMyChannels = pendingInvites.filter(
    (i) => i.isRequestToJoin && i.invitedProfileId !== currentUser.id
  );

  const totalInvites =
    invitesSentByMe.length +
    invitesToMe.length +
    joinRequestsToMyChannels.length;

  const respondToInvite = async (
    inviteId: number,
    response: "Accepted" | "Rejected"
  ) => {
    try {
      await ChannelService.respondToInvite(inviteId, { response });
      await fetchInvites();
      setSnackbar({
        open: true,
        message: `Invitación ${
          response === "Accepted" ? "aceptada" : "rechazada"
        }`,
        severity: response === "Accepted" ? "success" : "error",
        variant: "standard",
      });
    } catch (error) {
      console.error("Error al responder invitación:", error);
    }
  };

  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const handleShowChannels = () => {
    setShowChannels((prev) => !prev);
  };

  const handleShowPublicChannels = () => {
    setShowPublicChannels((prev) => !prev);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 0,
        borderRight: "1px solid",
        borderColor: "divider",
        width: "100%",
        bgcolor: "background.paper",
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: "text.primary" }}
          >
            Chats
          </Typography>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={() => setNotificationsOpen(true)}
              sx={{ color: "text.secondary" }}
            >
              <Badge badgeContent={totalInvites} color="error">
                <Notifications fontSize="small" />
              </Badge>
            </IconButton>
            <Dialog
              open={notificationsOpen}
              onClose={() => setNotificationsOpen(false)}
              fullWidth
            >
              <DialogTitle>Invitaciones pendientes</DialogTitle>
              <DialogContent dividers>
                {/* Invitaciones que enviaste */}
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Invitaciones que enviaste:
                </Typography>
                {invitesSentByMe.map((invite) => (
                  <ListItem
                    key={invite.id}
                    secondaryAction={
                      <IconButton
                        onClick={() => respondToInvite(invite.id, "Rejected")} // Usamos la misma función para rechazar
                        edge="end"
                        color="error"
                      >
                        <Close />
                      </IconButton>
                    }
                  >
                    <ListItemAvatar>
                      <Avatar src={invite.invitedProfileImage ?? undefined}>
                        {invite.invitedByUsername?.[0]?.toUpperCase() ?? "?"}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={`De ${
                        invite.invitedByUsername ?? "Usuario desconocido"
                      }`}
                      secondary={`Canal: ${invite.channelName}`}
                    />
                  </ListItem>
                ))}

                <Divider sx={{ my: 2 }} />

                {/* Invitaciones recibidas */}
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Invitaciones recibidas:
                </Typography>
                {invitesToMe.map((invite) => (
                  <ListItem
                    key={invite.id}
                    secondaryAction={
                      <>
                        <IconButton
                          onClick={() => respondToInvite(invite.id, "Accepted")}
                          edge="end"
                          color="success"
                        >
                          <Check />
                        </IconButton>
                        <IconButton
                          onClick={() => respondToInvite(invite.id, "Rejected")}
                          edge="end"
                          color="error"
                        >
                          <Close />
                        </IconButton>
                      </>
                    }
                  >
                    <ListItemAvatar>
                      <Avatar src={invite.invitedByProfileImage ?? undefined}>
                        {invite.invitedByUsername?.[0]?.toUpperCase() ?? "?"}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={`De ${
                        invite.invitedByUsername ?? "Usuario desconocido"
                      }`}
                      secondary={`Canal: ${invite.channelName}`}
                    />
                  </ListItem>
                ))}

                <Divider sx={{ my: 2 }} />

                {/* Solicitudes para tus canales */}
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Solicitudes para tus canales:
                </Typography>
                {joinRequestsToMyChannels.map((invite) => (
                  <ListItem
                    key={invite.id}
                    secondaryAction={
                      <>
                        <IconButton
                          onClick={() => respondToInvite(invite.id, "Accepted")}
                          edge="end"
                          color="success"
                        >
                          <Check />
                        </IconButton>
                        <IconButton
                          onClick={() => respondToInvite(invite.id, "Rejected")}
                          edge="end"
                          color="error"
                        >
                          <Close />
                        </IconButton>
                      </>
                    }
                  >
                    <ListItemAvatar>
                      <Avatar src={invite.invitedByProfileImage ?? undefined}>
                        {invite.invitedByUsername[0].toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={`De ${
                        invite.invitedByUsername ?? "Usuario desconocido"
                      }`}
                      secondary={`Solicita unirse a: ${invite.channelName}`}
                    />
                  </ListItem>
                ))}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setNotificationsOpen(false)}>
                  Cerrar
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        </Box>

        {/* User info */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: currentUser.id ? "success.main" : "grey.400",
            }}
          />
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, color: "text.primary" }}
          >
            {currentUser.username}
          </Typography>
        </Box>
      </Box>

      {/* Tabs */}
      <Tabs
        value={selectedTab}
        onChange={handleTabChange}
        variant="fullWidth"
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          "& .MuiTabs-indicator": {
            backgroundColor: "#308a46",
          },
          "& .MuiTab-root.Mui-selected": {
            color: "#308a46",
            fontWeight: 600,
          },
          "& .MuiTab-root": {
            textTransform: "none",
            fontWeight: 500,
            fontSize: "0.875rem",
          },
        }}
      >
        <Tab
          label="Canales"
          value="channels"
          icon={<TagOutlined fontSize="small" />}
          iconPosition="start"
        />
        <Tab
          label="Chat Online"
          value="global"
          icon={<Language fontSize="small" />}
          iconPosition="start"
        />
      </Tabs>

      {selectedTab === "channels" ? (
        <>
          {/* Search and Create */}
          <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <TextField
                size="small"
                placeholder="Buscar canales..."
                value={searchQuery}
                onChange={async (e) => {
                  const value = e.target.value;
                  setSearchQuery(value);

                  if (value.trim()) {
                    try {
                      const result = await ChannelService.searchChannels(
                        value,
                        currentUser.id
                      );
                      setSearchResults(result);
                    } catch (err) {
                      console.error("Error buscando canales:", err);
                      setSearchResults([]);
                    }
                  } else {
                    setSearchResults(null);
                  }
                }}
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search
                        fontSize="small"
                        sx={{ color: "text.secondary" }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
              <IconButton
                color="primary"
                onClick={() => setCreateDialogOpen(true)}
                sx={{
                  bgcolor: "#308a46",
                  color: "white",
                  "&:hover": { bgcolor: "#246a35" },
                  borderRadius: 2,
                }}
              >
                <Add fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          {/* Channels List */}
          <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
            <Box
              sx={{ display: "flex", alignItems: "center", p: 2 }}
              onClick={handleShowChannels}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, color: "text.primary", mr: 1 }}
              >
                Tus canales
              </Typography>
              {showChannels ? (
                <KeyboardArrowDown fontSize="small" />
              ) : (
                <KeyboardArrowUp fontSize="small" />
              )}
            </Box>
            <List
              sx={{ p: 1 }}
              style={{ display: showChannels ? "block" : "none" }}
            >
              {filteredChannels.map((channel) => (
                <ListItem key={channel.id} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    selected={selectedChannel?.id === channel.id}
                    onClick={() => onChannelSelect(channel)}
                    sx={{
                      borderRadius: 2,
                      mx: 1,
                      "&.Mui-selected": {
                        bgcolor: "#308a46",
                        color: "white",
                        "&:hover": {
                          bgcolor: "#246a35",
                        },
                      },
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        width: "100%",
                      }}
                    >
                      {/* Imagen del canal */}

                      <Avatar
                        src={channel.imageUrl || "/placeholder.svg"}
                        alt={channel.name}
                        sx={{
                          width: 38,
                          height: 38,
                          borderRadius: "50%",
                          objectFit: "fill",
                        }}
                      />

                      {/* Info del canal */}
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight:
                              selectedChannel?.id === channel.id ? 600 : 500,
                            fontSize: "0.875rem",
                          }}
                          noWrap
                        >
                          # {channel.name}
                        </Typography>
                      </Box>

                      {/* Chip de privacidad */}
                      {!channel.isPublic && (
                        <Chip
                          label="Privado"
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: "0.6875rem",
                            bgcolor:
                              selectedChannel?.id === channel.id
                                ? "rgba(255,255,255,0.2)"
                                : "warning.light",
                            color:
                              selectedChannel?.id === channel.id
                                ? "white"
                                : "warning.dark",
                          }}
                        />
                      )}
                    </Box>
                  </ListItemButton>
                </ListItem>
              ))}
              {filteredChannels.length === 0 && (
                <ListItem>
                  <ListItemText
                    primary="No se encontraron canales"
                    primaryTypographyProps={{
                      fontSize: "0.875rem",
                      color: "text.secondary",
                      textAlign: "center",
                    }}
                  />
                </ListItem>
              )}
            </List>
            <Box
              sx={{ display: "flex", alignItems: "center", p: 2 }}
              onClick={handleShowPublicChannels}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, color: "text.primary", mr: 1 }}
              >
                Canales públicos
              </Typography>
              {showPublicChannels ? (
                <KeyboardArrowDown fontSize="small" />
              ) : (
                <KeyboardArrowUp fontSize="small" />
              )}
            </Box>
            <List
              sx={{ p: 1 }}
              style={{ display: showPublicChannels ? "block" : "none" }}
            >
              {filteredPublicChannels.map((channel) => {
                if (
                  !channel.members?.some(
                    (member) => member.profileId === currentUser.id
                  )
                ) {
                  return (
                    <ListItem
                      key={channel.id}
                      disablePadding
                      sx={{ mb: 0.5 }}
                      onClick={() => {
                        setChannelJoinDialogOpen(true);
                        setChannelToJoin(channel);
                        handleShowChats();
                      }}
                    >
                      <ListItemButton
                        selected={selectedChannel?.id === channel.id}
                        onClick={() => onChannelSelect(channel)}
                        sx={{
                          borderRadius: 2,
                          mx: 1,
                          "&.Mui-selected": {
                            bgcolor: "#308a46",
                            color: "white",
                            "&:hover": {
                              bgcolor: "#246a35",
                            },
                          },
                          "&:hover": {
                            bgcolor: "action.hover",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            width: "100%",
                          }}
                        >
                          {/* Imagen del canal */}
                          <Avatar
                            src={channel.imageUrl || "/placeholder.svg"}
                            alt={channel.name}
                            sx={{
                              width: 38,
                              height: 38,
                              borderRadius: "50%",
                              objectFit: "fill",
                            }}
                          />

                          {/* Info del canal */}
                          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight:
                                  selectedChannel?.id === channel.id
                                    ? 600
                                    : 500,
                                fontSize: "0.875rem",
                              }}
                              noWrap
                            >
                              # {channel.name}
                            </Typography>
                            {channel.description && (
                              <Typography
                                variant="caption"
                                sx={{
                                  opacity: 0.8,
                                  display: "block",
                                  lineHeight: 1.2,
                                }}
                                noWrap
                              >
                                {channel.description}
                              </Typography>
                            )}
                          </Box>

                          {/* Chip de privacidad */}
                          {!channel.isPublic && (
                            <Chip
                              label="Privado"
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: "0.6875rem",
                                bgcolor:
                                  selectedChannel?.id === channel.id
                                    ? "rgba(255,255,255,0.2)"
                                    : "warning.light",
                                color:
                                  selectedChannel?.id === channel.id
                                    ? "white"
                                    : "warning.dark",
                              }}
                            />
                          )}
                        </Box>
                      </ListItemButton>
                    </ListItem>
                  );
                } else {
                  return null;
                }
              })}
              {filteredPublicChannels.length === 0 && (
                <ListItem>
                  <ListItemText
                    primary="No se encontraron canales"
                    primaryTypographyProps={{
                      fontSize: "0.875rem",
                      color: "text.secondary",
                      textAlign: "center",
                    }}
                  />
                </ListItem>
              )}
            </List>
          </Box>
        </>
      ) : (
        <Box sx={{ p: 3 }}>
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Language sx={{ fontSize: 48, color: "#308a46", mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Chat Online
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Conversaciones abiertas para toda la comunidad
            </Typography>
            {!isMdUp && (
              <Tooltip
                title="Haz clic para ver los mensajes"
                placement="top"
                arrow
              >
                <IconButton
                  onClick={handleShowChats}
                  sx={{
                    mt: 2,
                    bgcolor: "#308a46",
                    color: "white",
                    "&:hover": { bgcolor: "#307a46" },
                  }}
                >
                  <Chat />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />
        </Box>
      )}

      {/* Create Channel Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Crear nuevo canal</DialogTitle>

        <DialogContent sx={{ pb: 0 }}>
          <Card elevation={3} sx={{ borderRadius: 3 }}>
            <CardContent sx={{ px: 3, py: 3 }}>
              {/* Imagen */}
              <Box
                {...getRootProps()}
                sx={{
                  border: "2px dashed",
                  borderColor: isDragActive ? "primary.main" : "divider",
                  borderRadius: "50%",
                  width: 120,
                  height: 120,
                  mx: "auto",
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "background.default",
                  cursor: "pointer",
                  transition: "border-color 0.3s ease",
                  mb: 2,
                }}
              >
                <input {...getInputProps()} />
                {previewImage ? (
                  <>
                    <Avatar
                      src={previewImage}
                      alt="Preview"
                      sx={{ width: 112, height: 112 }}
                    />
                    <IconButton
                      onClick={handleRemoveImage}
                      size="small"
                      sx={{
                        position: "absolute",
                        top: -10,
                        right: -10,
                        bgcolor: "white",
                        boxShadow: 1,
                        "&:hover": { bgcolor: "grey.100" },
                        zIndex: 1,
                      }}
                      disabled={isSubmitting}
                    >
                      <Cancel fontSize="small" color="error" />
                    </IconButton>
                  </>
                ) : (
                  <Box sx={{ textAlign: "center" }}>
                    <CloudUpload
                      sx={{ fontSize: 32, color: "text.secondary", mb: 1 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Arrastra una imagen o haz clic para subir
                    </Typography>
                  </Box>
                )}
              </Box>
              {/* Nombre del canal */}
              <TextField
                autoFocus
                label="Nombre del canal"
                fullWidth
                size="small"
                value={newChannelName}
                onChange={(e) => setNewChannelName(e.target.value)}
                helperText="El nombre se convertirá automáticamente a minúsculas"
                sx={{ mb: 2 }}
                InputProps={{ sx: { borderRadius: 2 } }}
              />

              {/* Descripción */}
              <TextField
                label="Descripción"
                fullWidth
                multiline
                size="small"
                value={newChannelDescription}
                onChange={(e) => setNewChannelDescription(e.target.value)}
                sx={{ mb: 2 }}
                InputProps={{ sx: { borderRadius: 2 } }}
              />

              {/* Enlace de WhatsApp */}
              <TextField
                label="Enlace de WhatsApp"
                fullWidth
                size="small"
                value={whatsappLink}
                onChange={(e) => setWhatsappLink(e.target.value)}
                helperText="Opcional. Ejemplo: https://chat.whatsapp.com/..."
                sx={{ mb: 2 }}
                InputProps={{ sx: { borderRadius: 2 } }}
              />

              {/* Switches */}
              <FormControlLabel
                control={
                  <Switch
                    checked={isPublic}
                    onChange={(e) => setIsPublic(e.target.checked)}
                  />
                }
                label="Canal público"
                sx={{ mb: 1 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={requiresInvite}
                    onChange={(e) => setRequiresInvite(e.target.checked)}
                    disabled={!isPublic}
                  />
                }
                label="Requiere invitación"
                sx={{ mb: 2 }}
              />
            </CardContent>
          </Card>
        </DialogContent>

        <DialogActions sx={{ px: 3, pt: 2, pb: 3 }}>
          <Button
            onClick={handleCreateChannel}
            variant="contained"
            color="primary"
            fullWidth
            startIcon={<AddCircleOutline />}
            disabled={!newChannelName.trim() || isSubmitting}
            sx={{ borderRadius: 3, fontWeight: 600, py: 1.4 }}
          >
            {isSubmitting ? (
              <CircularProgress size={24} sx={{ color: "#fff" }} />
            ) : (
              "Crear canal"
            )}
          </Button>
        </DialogActions>

        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError(null)}
        >
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Snackbar>
      </Dialog>
      <ChannelJoinDialog
        open={channelJoinDialogOpen}
        onClose={() => setChannelJoinDialogOpen(false)}
        onConfirm={async () => {
          console.log("channeljoin", channelToJoin);
          if (!channelToJoin?.requiresInvite) {
            try {
              const response = await ChannelService.joinPublicChannel(
                channelToJoin?.id ?? 0,
                {
                  profileId: currentUser.id,
                }
              );
              if (response.success) {
                setSnackbar({
                  open: true,
                  message: response.message,
                  severity: "success",
                  variant: "standard",
                });
                await fetchChannels();
                await fetchPublicChannels();
                setSelectedChannel(channelToJoin!);
                await fetchChannelData();
                setSelectedChannel(null);
              }
            } catch (error: any) {
              setSnackbar({
                open: true,
                message: error.message,
                severity: "error",
                variant: "standard",
              });
              return;
            }
          } else {
            try {
              const response = await ChannelService.inviteToChannel(
                channelToJoin?.id ?? 0,
                {
                  invitedByProfileId: currentUser.id,
                  invitedProfileId: currentUser.id,
                  isRequestToJoin: true,
                }
              );
              if (response.success) {
                setSnackbar({
                  open: true,
                  message: response.message,
                  severity: "success",
                  variant: "standard",
                });
                onChannelSelect(channelToJoin);
              }
            } catch (error: any) {
              setSnackbar({
                open: true,
                message: error.message || "Error al solicitar unirse al canal",
                severity: "error",
                variant: "standard",
              });
              return;
            }
          }
          setChannelJoinDialogOpen(false);
        }}
        channel={channelToJoin}
        isPublic={channelToJoin?.requiresInvite ? false : true}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          variant={snackbar.variant}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
}
